import type {AttributeMetaData} from 'src/types/writeback';
import type {EditableNode, EditableGraph} from 'src/types/chatbot';
import type {ReducerState} from './reducer';
import type {Validation} from 'src/utils/validation';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {finalizeReducerState} from './reducer';

import Popover from 'src/components/conversation-builder/popover';
import {ButtonsSmall} from 'src/components/conversation-builder/form';
import Button from 'src/components/lib/new-button';
import OneLineTextInput from 'src/components/lib/text-input/one-line';
import {PadSection} from './common';

import css from 'src/components/conversation-builder/popover.css';


export default function EditorContainer<N extends EditableNode>({
  attributeWritebackInfo,
  onSubmit,
  disabled,
  onUpdate,
  children,
  ...props
}: {
  state: ReducerState<N>;
  actionName: 'add' | 'edit';
  onClose: () => unknown;
  readOnly?: unknown;
  className?: string;
  error: Validation | null | undefined;
  children: React.ReactNode;
  attributeWritebackInfo?: AttributeMetaData | null | undefined;
  onSubmit: (arg1: N) => unknown;
  onUpdate: (arg1: Partial<N>) => unknown;
  disabled?: unknown;
}) {
  const {state, actionName, onClose, readOnly, error} = props;
  const {t} = useLabelTranslation();

  const handleSubmit = () => {
    onSubmit(finalizeReducerState(state, attributeWritebackInfo));
  };

  return (
    // @ts-expect-error - TS2345 - Argument of type '{ name: string; }' is not assignable to parameter of type 'Partial<N>'.
    <NodePopover {...props} onChangeName={(name) => onUpdate({name})}>
      <form
        className={css.body}
        onSubmit={(event) => {
          event.preventDefault();
          if (!readOnly && !disabled) {
            handleSubmit();
          }
        }}
      >
        {children}
        <PadSection>
          <ButtonsSmall>
            <Button size="medium" onClick={onClose}>
              {readOnly ? t('Done', 'Done') : t('Cancel', 'Cancel')}
            </Button>
            {!readOnly && (
              <Button
                type="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={disabled || error}
              >
                {actionName === 'add' ? t('Add', 'Add') : t('Done', 'Done')}
              </Button>
            )}
          </ButtonsSmall>
        </PadSection>
      </form>
    </NodePopover>
  );
}

/*
function BasicEditor(): React.Node {
  return (
      <form
        className={css.body}
        onSubmit={(event) => {
          event.preventDefault();
          if (!readOnly && !disabled) {
            handleSubmit();
          }
        }}
      >
        {children}
        <PadSection>
          <ButtonsSmall>
            <Button size="medium" onClick={onClose}>
              {readOnly ? 'Done' : 'Cancel'}
            </Button>
            {!readOnly && (
              <Button
                type="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={disabled || error}
              >
                {actionName === 'add' ? 'Add' : 'Done'}
              </Button>
            )}
          </ButtonsSmall>
        </PadSection>
      </form>
  );
}
*/

export type NodePopoverProps<N extends EditableNode> = {
  state: ReducerState<N>;
  actionName: 'add' | 'edit';
  onChangeName: (arg1: string) => unknown;
  onClose: () => unknown;
  readOnly?: unknown;
  className?: string;
  error: Validation | null | undefined;
  children: React.ReactNode;
};

export function NodePopover<N extends EditableNode>({
  state,
  actionName,
  onChangeName,
  onClose,
  readOnly,
  className,
  error,
  children,
}: NodePopoverProps<N>) {
  const {t} = useLabelTranslation();
  const {node} = state;
  const nameErrors = error?.name?.errors;
  return (
    <Popover
      className={className}
      titleText={
        actionName === 'add'
          ? t('Add_node', 'Add node')
          : t('Edit_node', 'Edit node')
      }
      titleContent={
        <>
          {/* @ts-expect-error TS2786 */}
          <OneLineTextInput
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
            className={css.nameInput}
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
            placeholder={`${t('Name_this_node', 'Name this node')}...`}
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
            value={node.name}
            // @ts-expect-error - TS2322 - Type '(event: ChangeEvent<HTMLInputElement>) => unknown' is not assignable to type 'never'.
            onChange={(event) => onChangeName(event.currentTarget.value)}
            // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'never'.
            maxLength={40}
            // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'never'.
            error={nameErrors?.[0]}
            // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'never'.
            disabled={readOnly}
          />
          <span className={css.nameInputInfo}>
            {nameErrors
              ? nameErrors.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique',
                  'Node names must be unique',
                )}. ${t('40_characters_maximum', '40 characters maximum')}.`}
          </span>
        </>
      }
      onClose={onClose}
    >
      {children}
    </Popover>
  );
}
